import type { GetServerSideProps, NextPage } from 'next';
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote';
import { serialize } from 'next-mdx-remote/serialize';
import Head from 'next/head';

import RegistrationForm from '@/components/registration/RegistrationForm';
import { db } from '@/lib/db';
import { groups } from '@/lib/schema';
import mdxComponents from '@/utils/mdxComponents';

const introductionText = `
  Herzlich Willkommen zur Sponsorenseite der Royal Rangers aus Mittelfranken (BY2)!

  Hier hast du die Möglichkeit, dich als Sponsor für einen bestimmten Läufer einzutragen und unsere Stämme damit finanziell zu unterstützen.

  ### Wie geht das?

  Der von dir gesponserte Läufer nimmt am 25. April 2026 am Sponsorenlauf im Tennenloher Forst bei Erlangen teil und läuft dort bis zu 8 Stunden eine vorgegebene flache Strecke. Die Kinder, Jugendlichen, Leiter und Freunde der Royal Rangers scheuen keine Anstrengung, laufen Kilometer für Kilometer und nehmen manche Blase in Kauf. Du honorierst das mit einem vorher vereinbarten Spendenbetrag pro Kilometer, den der Stammposten dann für seine Zwecke investieren kann.

  **Wir freuen uns über jeden Sponsor! Jeder kleine und große Geldbetrag ist uns willkommen und wird umsichtig für die Kinder- und Jugendarbeit ausgegeben!**

  ### Wie kann ich Sponsor werden?

  Wenn du mitmachen möchtest, brauchst du nur das Formular auf dieser Seite ausfüllen. Wenn du auf "Absenden" gedrückt hast, wirst du eine Bestätigungsemail erhalten. Nach dem 25. April 2026 wirst du das Ergebnis deines Läufers und eine Zahlungsaufforderung erhalten.  Dazu werden wir dich per Email kontaktieren. Für Spenden ab 200€ werden wir außerdem am Jahresende eine Spendenquittung verschicken (Spenden bis 200€ können mit dem Kontoauszug als Nachweis bei der Einkommenssteuer geltend gemacht werden).

  Falls du generelle Fragen hast, kannst du dich gerne an deinen Ansprechpartner im jeweiligen Stamm wenden.

  Vielen herzlichen Dank!

  Wir wünschen dir allzeit gut Pfad und Gottes Segen!
`;

type HomePageProps = {
  introductionTextSource: MDXRemoteSerializeResult;
  groups: {
    id: string;
    name: string;
    registrationPageTextSource: MDXRemoteSerializeResult;
  }[];
  initialGroup: string | null;
};

export const getServerSideProps: GetServerSideProps<HomePageProps> = async ({
  query,
}) => {
  const groupsList = await db.select().from(groups);
  const mappedGroups = await Promise.all(
    groupsList.map(async (g) => ({
      id: g.id,
      name: g.name,
      registrationPageTextSource: await serialize(g.registrationPageText),
    })),
  );

  const initialGroup = query.group?.toString() ?? null;
  const initialGroupExists =
    groupsList.findIndex((group) => group.id === initialGroup) !== -1;

  return {
    props: {
      introductionTextSource: await serialize(introductionText),
      groups: mappedGroups,
      initialGroup: initialGroupExists ? initialGroup : null,
    },
  };
};

const HomePage: NextPage<HomePageProps> = ({
  introductionTextSource,
  groups,
  initialGroup,
}) => (
  <>
    <Head>
      <title>Royal Rangers Sponsorenlauf</title>
    </Head>

    <div className="container mx-auto min-h-full max-w-3xl">
      <div className="flex h-full flex-col justify-center">
        <h1 className="mb-8 text-3xl font-semibold tracking-tight text-balance">
          Sponsoren Anmeldung
        </h1>

        <MDXRemote {...introductionTextSource} components={mdxComponents} />

        <RegistrationForm
          groups={groups}
          initialGroup={initialGroup || undefined}
        />
      </div>
    </div>
  </>
);

export default HomePage;
